import { useMemo, useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from "react";

const COGNET_CSS = `
  /* Linear-like: crisp, minimal */
  .cn-node { will-change: transform, box-shadow; }

  @keyframes cn-breathe {
    0%,100% { transform: scale(var(--base-scale, 1)); }
    50% { transform: scale(calc(var(--base-scale, 1) * 1.06)); }
  }

  .cn-breathe {
    animation: cn-breathe 2.8s ease-in-out infinite;
  }
`;

const mainNodes = [
  { id: "relativity",    label: "相对论",   x: 50, y: 46, r: 40, color: "#111827", fc: "cn-fa" },
  { id: "photoelectric", label: "光电效应", x: 33, y: 27, r: 32, color: "#0EA5E9", fc: "cn-fb" },
  { id: "statistical",   label: "统计力学", x: 67, y: 27, r: 32, color: "#F97316", fc: "cn-fc" },
  { id: "quantum",       label: "量子力学", x: 18, y: 40, r: 32, color: "#8B5CF6", fc: "cn-fa2" },
  { id: "scientific",    label: "科学方法", x: 76, y: 50, r: 30, color: "#EC4899", fc: "cn-fb2" },
  { id: "cosmology",     label: "宇宙论",   x: 18, y: 60, r: 30, color: "#3B82F6", fc: "cn-fc2" },
  { id: "philosophy",    label: "哲学",     x: 28, y: 75, r: 28, color: "#EAB308", fc: "cn-fa" },
  { id: "math",          label: "数学",     x: 50, y: 75, r: 30, color: "#10B981", fc: "cn-fb" },
  { id: "brownian",      label: "布朗运动", x: 67, y: 68, r: 28, color: "#06B6D4", fc: "cn-fc" },
];

const leafNodes = [
  // 量子力学(18,40) — 扇形方向：正西/西北，y 全部 < 50
  { id: "wavefunc",      label: "波函数",       x: 3,  y: 28, pColor: "#8B5CF6", parent: "quantum" },
  { id: "quantumstate",  label: "量子态",       x: 5,  y: 34, pColor: "#8B5CF6", parent: "quantum" },
  { id: "photonstate",   label: "光子态",       x: 3,  y: 41, pColor: "#8B5CF6", parent: "quantum" },
  { id: "compton",       label: "康普顿效应",   x: 8,  y: 31, pColor: "#8B5CF6", parent: "quantum" },
  { id: "uncertainty",   label: "不确定性原理", x: 3,  y: 47, pColor: "#8B5CF6", parent: "quantum" },
  { id: "waveparticle",  label: "波粒二象性",   x: 10, y: 46, pColor: "#8B5CF6", parent: "quantum" },

  // 光电效应(33,27) — 扇形方向：正北/西北，y < 25
  { id: "quantumhyp",    label: "光量子假说",   x: 22, y: 14, pColor: "#0EA5E9", parent: "photoelectric" },
  { id: "escapeWork",    label: "逸出功",       x: 28, y: 18, pColor: "#0EA5E9", parent: "photoelectric" },
  { id: "einsteinEq",    label: "爱因斯坦方程", x: 34, y: 12, pColor: "#0EA5E9", parent: "photoelectric" },
  { id: "lightparticle", label: "光的粒子性",   x: 42, y: 17, pColor: "#0EA5E9", parent: "photoelectric" },
  { id: "amplify",       label: "增强原理",     x: 40, y: 22, pColor: "#0EA5E9", parent: "photoelectric" },

  // 统计力学(67,27) — 扇形方向：正北/东北，y < 26
  { id: "thermo",        label: "热力学涨落",   x: 57, y: 13, pColor: "#F97316", parent: "statistical" },
  { id: "boltzmann",     label: "玻尔兹曼分布", x: 67, y: 11, pColor: "#F97316", parent: "statistical" },
  { id: "partition",     label: "配分函数",     x: 76, y: 15, pColor: "#F97316", parent: "statistical" },
  { id: "maxwell",       label: "麦克斯韦分布", x: 80, y: 22, pColor: "#F97316", parent: "statistical" },

  // 相对论(50,46) — 近邻叶节点，分布在节点周围空隙
  { id: "equiv",         label: "等效原理",     x: 46, y: 35, pColor: "#111827", parent: "relativity" },
  { id: "spacetime",     label: "时空曲率",     x: 58, y: 35, pColor: "#111827", parent: "relativity" },
  { id: "lorentz",       label: "洛伦兹变换",   x: 37, y: 42, pColor: "#111827", parent: "relativity" },
  { id: "special",       label: "狭义相对论",   x: 40, y: 55, pColor: "#111827", parent: "relativity" },
  { id: "general",       label: "广义相对论",   x: 47, y: 58, pColor: "#111827", parent: "relativity" },
  { id: "emc2",          label: "质能关系",     x: 57, y: 55, pColor: "#111827", parent: "relativity" },

  // 科学方法(76,50) — 扇形方向：正东/东南，x > 82
  { id: "thought",       label: "思想实验",     x: 88, y: 40, pColor: "#EC4899", parent: "scientific" },
  { id: "simplicity",    label: "简洁性原则",   x: 91, y: 50, pColor: "#EC4899", parent: "scientific" },
  { id: "expverify",     label: "实验验证",     x: 88, y: 58, pColor: "#EC4899", parent: "scientific" },
  { id: "logic",         label: "逻辑推理",     x: 85, y: 63, pColor: "#EC4899", parent: "scientific" },
  { id: "unity",         label: "统一性追求",   x: 81, y: 67, pColor: "#EC4899", parent: "scientific" },

  // 宇宙论(18,60) — 扇形方向：正西/西南，y 全部 > 53
  { id: "steadystate",   label: "稳态模型",     x: 4,  y: 55, pColor: "#3B82F6", parent: "cosmology" },
  { id: "cosmoconst",    label: "宇宙常数",     x: 4,  y: 63, pColor: "#3B82F6", parent: "cosmology" },
  { id: "timearrow",     label: "时间箭头",     x: 8,  y: 69, pColor: "#3B82F6", parent: "cosmology" },
  { id: "largescale",    label: "大尺度结构",   x: 5,  y: 74, pColor: "#3B82F6", parent: "cosmology" },
  { id: "cosmoexp",      label: "宇宙膨胀",     x: 14, y: 75, pColor: "#3B82F6", parent: "cosmology" },
  { id: "gravfield",     label: "引力场方程",   x: 23, y: 70, pColor: "#3B82F6", parent: "cosmology" },

  // 哲学(28,75) — 扇形方向：西南/正南
  { id: "realism",       label: "实在论",       x: 14, y: 80, pColor: "#EAB308", parent: "philosophy" },
  { id: "freewill",      label: "自由意志",     x: 19, y: 83, pColor: "#EAB308", parent: "philosophy" },
  { id: "sciPhil",       label: "科学哲学",     x: 25, y: 86, pColor: "#EAB308", parent: "philosophy" },
  { id: "ethics",        label: "伦理与责任",   x: 32, y: 87, pColor: "#EAB308", parent: "philosophy" },
  { id: "rational",      label: "理性思维",     x: 37, y: 84, pColor: "#EAB308", parent: "philosophy" },

  // 数学(50,75) — 扇形方向：正南
  { id: "linAlg",        label: "线性代数",     x: 43, y: 84, pColor: "#10B981", parent: "math" },
  { id: "diffgeo",       label: "微分几何",     x: 41, y: 88, pColor: "#10B981", parent: "math" },
  { id: "pde",           label: "偏微分方程",   x: 50, y: 90, pColor: "#10B981", parent: "math" },
  { id: "variation",     label: "变分法",       x: 57, y: 84, pColor: "#10B981", parent: "math" },
  { id: "tensor",        label: "张量分析",     x: 59, y: 88, pColor: "#10B981", parent: "math" },

  // 布朗运动(67,68) — 扇形方向：东南，x > 63 且 y > 68
  { id: "thermo2",       label: "热力学证据",   x: 63, y: 77, pColor: "#06B6D4", parent: "brownian" },
  { id: "molecular",     label: "分子运动论",   x: 70, y: 80, pColor: "#06B6D4", parent: "brownian" },
  { id: "diffusion",     label: "扩散方程",     x: 77, y: 75, pColor: "#06B6D4", parent: "brownian" },
  { id: "random",        label: "随机过程",     x: 79, y: 71, pColor: "#06B6D4", parent: "brownian" },
];

const edges = [
  { from: "relativity", to: "photoelectric", w: 3 },
  { from: "relativity", to: "statistical",   w: 3 },
  { from: "relativity", to: "quantum",       w: 3 },
  { from: "relativity", to: "scientific",    w: 2 },
  { from: "relativity", to: "cosmology",     w: 2 },
  { from: "relativity", to: "philosophy",    w: 2 },
  { from: "relativity", to: "math",          w: 3 },
  { from: "relativity", to: "brownian",      w: 2 },
  { from: "quantum",    to: "photoelectric", w: 2 },
  { from: "quantum",    to: "philosophy",    w: 1 },
  { from: "statistical", to: "brownian",     w: 2 },
  { from: "statistical", to: "scientific",   w: 1 },
  { from: "math",       to: "philosophy",    w: 1 },
  { from: "math",       to: "brownian",      w: 2 },
  { from: "math",       to: "cosmology",     w: 1 },
  { from: "cosmology",  to: "philosophy",    w: 1 },
];

const nodeDomainMap: Record<string, string> = {
  relativity: "相对论",
  photoelectric: "光电效应",
  statistical: "统计力学",
  quantum: "量子力学",
  scientific: "科学方法",
  cosmology: "宇宙论",
  philosophy: "哲学",
  math: "数学",
  brownian: "布朗运动",
  methods: "科学方法",
  logic: "逻辑学",
  statistics: "统计学",
  epistemology: "认识论",
  ethics: "科研伦理",
  communication: "学术传播",
  history: "科学史",
  experiment: "实验设计",
};

export const COGNET_MAIN_NODES = mainNodes;
export const COGNET_LEAF_NODES = leafNodes;
export const COGNET_NODE_DOMAIN_MAP = nodeDomainMap;
export const COGNET_EDGES = edges;

const compareMainNodes = [
  { id: "methods", label: "科学方法", x: 50, y: 46, r: 40, color: "#111827", fc: "cn-fa" },
  { id: "logic", label: "逻辑学", x: 33, y: 27, r: 32, color: "#0EA5E9", fc: "cn-fb" },
  { id: "statistics", label: "统计学", x: 67, y: 27, r: 32, color: "#F97316", fc: "cn-fc" },
  { id: "epistemology", label: "认识论", x: 18, y: 40, r: 32, color: "#8B5CF6", fc: "cn-fa2" },
  { id: "ethics", label: "科研伦理", x: 76, y: 50, r: 30, color: "#EC4899", fc: "cn-fb2" },
  { id: "communication", label: "学术传播", x: 18, y: 60, r: 30, color: "#3B82F6", fc: "cn-fc2" },
  { id: "history", label: "科学史", x: 28, y: 75, r: 28, color: "#EAB308", fc: "cn-fa" },
  { id: "math", label: "数学", x: 50, y: 75, r: 30, color: "#10B981", fc: "cn-fb" },
  { id: "experiment", label: "实验设计", x: 67, y: 68, r: 28, color: "#06B6D4", fc: "cn-fc" },
];

const compareLeafNodes = [
  { id: "deduction", label: "演绎推理", x: 3, y: 28, pColor: "#0EA5E9", parent: "logic" },
  { id: "induction", label: "归纳推理", x: 5, y: 34, pColor: "#0EA5E9", parent: "logic" },
  { id: "bayes", label: "贝叶斯方法", x: 3, y: 41, pColor: "#F97316", parent: "statistics" },
  { id: "sampling", label: "抽样理论", x: 8, y: 31, pColor: "#F97316", parent: "statistics" },
  { id: "knowledge", label: "知识结构", x: 3, y: 47, pColor: "#8B5CF6", parent: "epistemology" },
  { id: "paradigm", label: "范式转换", x: 10, y: 46, pColor: "#8B5CF6", parent: "epistemology" },
  { id: "review", label: "同行评议", x: 22, y: 14, pColor: "#EC4899", parent: "ethics" },
  { id: "integrity", label: "学术诚信", x: 28, y: 18, pColor: "#EC4899", parent: "ethics" },
  { id: "popularization", label: "科普传播", x: 34, y: 12, pColor: "#3B82F6", parent: "communication" },
  { id: "archives", label: "科学档案", x: 42, y: 17, pColor: "#EAB308", parent: "history" },
  { id: "trial", label: "实验试错", x: 57, y: 13, pColor: "#06B6D4", parent: "experiment" },
  { id: "control", label: "对照组", x: 67, y: 11, pColor: "#06B6D4", parent: "experiment" },
];

const compareEdges = [
  { from: "methods", to: "logic", w: 3 },
  { from: "methods", to: "statistics", w: 3 },
  { from: "methods", to: "epistemology", w: 3 },
  { from: "methods", to: "ethics", w: 2 },
  { from: "methods", to: "communication", w: 2 },
  { from: "methods", to: "history", w: 2 },
  { from: "methods", to: "math", w: 3 },
  { from: "methods", to: "experiment", w: 2 },
  { from: "logic", to: "statistics", w: 2 },
  { from: "logic", to: "history", w: 1 },
  { from: "statistics", to: "experiment", w: 2 },
  { from: "statistics", to: "ethics", w: 1 },
  { from: "math", to: "communication", w: 1 },
  { from: "math", to: "experiment", w: 2 },
  { from: "math", to: "history", w: 1 },
  { from: "communication", to: "history", w: 1 },
];

export const COGNET_COMPARE_MAIN_NODES = compareMainNodes;
export const COGNET_COMPARE_LEAF_NODES = compareLeafNodes;
export const COGNET_COMPARE_EDGES = compareEdges;

function getN(id: string, variant: "mine" | "compare") {
  const nodes = variant === "compare" ? compareMainNodes : mainNodes;
  return nodes.find((n) => n.id === id)!;
}

export interface CogNetGraphCanvasRef {
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
}

interface Props {
  selectedNode: string | null;
  onSelectNode: (id: string | null) => void;
  compareMode?: boolean;
  showLabels?: boolean;
  showEdges?: boolean;
  smartLayout?: boolean;
  activeDomains?: string[];
  visibleMainNodeIds?: string[];
  visibleLeafNodeIds?: string[];
  commonLabels?: string[];
  commonColorMap?: Record<string, string>;
  theme?: "light" | "dark";
}

const CogNetGraphCanvas = forwardRef<CogNetGraphCanvasRef, Props>(
  (
    {
      selectedNode,
      onSelectNode,
      compareMode = false,
      showLabels = true,
      showEdges = true,
      smartLayout = true,
      activeDomains = [],
      visibleMainNodeIds,
      visibleLeafNodeIds,
      commonLabels,
      commonColorMap,
      theme = "light",
    },
    ref,
  ) => {
    const [hovered, setHovered] = useState<string | null>(null);
    const [selectedLeaf, setSelectedLeaf] = useState<string | null>(null);
    const [zoom, setZoom] = useState(smartLayout ? 1 : 0.95);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const panStart = useRef({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      zoomIn: () => setZoom((z) => Math.min(+(z * 1.25).toFixed(3), 4)),
      zoomOut: () => setZoom((z) => Math.max(+(z / 1.25).toFixed(3), 0.25)),
      reset: () => { setZoom(1); setPan({ x: 0, y: 0 }); },
    }));

    const handleWheel = useCallback((e: WheelEvent) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.85 : 1.18;
      setZoom((prevZoom) => {
        const newZoom = Math.min(Math.max(prevZoom * delta, 0.25), 4);
        setPan((prevPan) => ({
          x: mouseX - (mouseX - prevPan.x) * (newZoom / prevZoom),
          y: mouseY - (mouseY - prevPan.y) * (newZoom / prevZoom),
        }));
        return +newZoom.toFixed(3);
      });
    }, []);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      el.addEventListener("wheel", handleWheel, { passive: false });
      return () => el.removeEventListener("wheel", handleWheel);
    }, [handleWheel]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-node]")) return;
      isDragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      panStart.current = { ...pan };
    }, [pan]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      setPan({ x: panStart.current.x + dx, y: panStart.current.y + dy });
    }, []);

    const handleMouseUp = useCallback(() => {
      isDragging.current = false;
    }, []);

    const visibleDomainSet = new Set(activeDomains);
    const hasDomainFilter = activeDomains.length > 0;
    const hasMainIdFilter = Array.isArray(visibleMainNodeIds) && visibleMainNodeIds.length > 0;
    const hasLeafIdFilter = Array.isArray(visibleLeafNodeIds) && visibleLeafNodeIds.length > 0;
    const visibleMainIdSet = useRef<Set<string>>(new Set());
    const visibleLeafIdSet = useRef<Set<string>>(new Set());

    // keep sets updated without re-allocating in hot paths
    visibleMainIdSet.current = new Set(visibleMainNodeIds ?? []);
    visibleLeafIdSet.current = new Set(visibleLeafNodeIds ?? []);

    const isNodeVisible = (id: string) => {
      if (hasMainIdFilter && !visibleMainIdSet.current.has(id)) return false;
      if (!hasDomainFilter) return true;
      const domain = nodeDomainMap[id];
      return visibleDomainSet.has(domain);
    };

    const isLeafVisible = (leafId: string, parent: string) => {
      if (!isNodeVisible(parent)) return false;
      if (!hasLeafIdFilter) return true;
      return visibleLeafIdSet.current.has(leafId);
    };

    const activeEdges = compareMode ? compareEdges : edges;

    // If a leaf node is selected, highlight based on its parent main node,
    // but still let the page use the leaf id for showing details.
    const selectedMainId = useMemo(() => {
      if (!selectedNode) return null;
      const list = compareMode ? compareLeafNodes : leafNodes;
      const lf = list.find((l) => l.id === selectedNode);
      return lf ? lf.parent : selectedNode;
    }, [selectedNode, compareMode]);

    const edgeVisible = (e: (typeof edges)[0]) => isNodeVisible(e.from) && isNodeVisible(e.to);

    const edgeHi = (e: (typeof edges)[0]) =>
      !selectedMainId || e.from === selectedMainId || e.to === selectedMainId;

    const nodeHi = (id: string) => {
      if (!selectedMainId) return true;
      if (id === selectedMainId) return true;
      return activeEdges.some(
        (e) =>
          (e.from === id && e.to === selectedMainId) ||
          (e.to === id && e.from === selectedMainId)
      );
    };

    const accent = "#5E6AD2"; // Linear-like purple/indigo accent
    const commonSet = useMemo(() => new Set(commonLabels ?? []), [commonLabels]);
    const getCommonColor = useCallback(
      (label: string) => commonColorMap?.[label] ?? accent,
      [commonColorMap],
    );

    const toRgb = (hex: string) => {
      const h = hex.replace("#", "").trim();
      if (h.length !== 6) return null;
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      if ([r, g, b].some((v) => Number.isNaN(v))) return null;
      return { r, g, b };
    };

    const rgbaHex = (hex: string, a: number) => {
      const rgb = toRgb(hex);
      if (!rgb) return hex;
      const clamped = Math.min(1, Math.max(0, a));
      return `rgba(${rgb.r},${rgb.g},${rgb.b},${clamped})`;
    };

    const mixHex = (a: string, b: string, t: number) => {
      const A = toRgb(a);
      const B = toRgb(b);
      if (!A || !B) return a;
      const clamped = Math.min(1, Math.max(0, t));
      const r = Math.round(A.r * (1 - clamped) + B.r * clamped);
      const g = Math.round(A.g * (1 - clamped) + B.g * clamped);
      const bl = Math.round(A.b * (1 - clamped) + B.b * clamped);
      const to2 = (v: number) => v.toString(16).padStart(2, "0");
      return `#${to2(r)}${to2(g)}${to2(bl)}`;
    };

    // Make "common" highlight color deeper but still tied to the node's own hue.
    const deepCommon = (base: string) => mixHex(base, "#4070FF", 0.55);
    // Dark-mode solid background: keep node hue, normalize brightness toward #4070FF
    const darkSolid = (base: string) => mixHex(base, "#4070FF", 0.48);

    const colors =
      theme === "dark"
        ? {
            bg: "#0B0F1A",
            grid: "rgba(148,163,184,0.14)",
            edgeNeutral: "rgba(226,232,240,0.22)",
            edgeMuted: "rgba(226,232,240,0.10)",
            text: "rgba(248,250,252,0.92)",
            textMuted: "rgba(226,232,240,0.62)",
            nodeStroke: "rgba(226,232,240,0.14)",
            leafFill: "rgba(15,23,42,0.55)",
          }
        : {
            bg: "#FAFAFB",
            grid: "rgba(17,24,39,0.08)",
            edgeNeutral: "rgba(17,24,39,0.16)",
            edgeMuted: "rgba(17,24,39,0.06)",
            text: "#111827",
            textMuted: "rgba(17,24,39,0.60)",
            nodeStroke: "rgba(17,24,39,0.14)",
            leafFill: "rgba(255,255,255,0.95)",
          };

    const visibleLeafForCanvas = useMemo(() => {
      const list = compareMode ? compareLeafNodes : leafNodes;
      if (!hasLeafIdFilter) return list;
      const set = visibleLeafIdSet.current;
      return list.filter((l) => set.has(l.id));
    }, [compareMode, hasLeafIdFilter]);

    return (
      <div
        ref={containerRef}
        className="relative flex-1 h-full min-h-0 overflow-hidden"
        style={{
          background: colors.bg,
          cursor: isDragging.current ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={() => setSelectedLeaf(null)}
      >
        <style>{COGNET_CSS}</style>

        {/* Static dot grid background — fixed, not transformed */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle, ${colors.grid} 1px, transparent 1px)`,
            backgroundSize: "28px 28px",
            opacity: theme === "dark" ? 0.22 : 0.18,
          }}
        />

        {/* Dark-mode ambient gradient glow (Linear-like) */}
        {theme === "dark" && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(900px 420px at 20% 25%, rgba(94,106,210,0.18) 0%, rgba(94,106,210,0.06) 35%, rgba(11,15,26,0) 70%)," +
                "radial-gradient(700px 520px at 70% 55%, rgba(16,185,129,0.10) 0%, rgba(16,185,129,0.04) 40%, rgba(11,15,26,0) 75%)," +
                "radial-gradient(520px 520px at 55% 30%, rgba(236,72,153,0.10) 0%, rgba(236,72,153,0.04) 45%, rgba(11,15,26,0) 78%)",
              opacity: 1,
              mixBlendMode: "screen",
            }}
          />
        )}

        {/* Transformable canvas */}
        <div
          className="absolute inset-0"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            willChange: "transform",
          }}
        >
          {/* SVG edges */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ overflow: "visible" }}
          >
            <defs>
              {visibleLeafForCanvas.map((l) => {
                const p = getN(l.parent, compareMode ? "compare" : "mine");
                return (
                  <linearGradient
                    key={`cnlg-${l.id}`}
                    id={`cnlg-${l.id}`}
                    x1={`${p.x}%`}
                    y1={`${p.y}%`}
                    x2={`${l.x}%`}
                    y2={`${l.y}%`}
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0%" stopColor={colors.edgeNeutral} stopOpacity="1" />
                    <stop offset="100%" stopColor={colors.edgeMuted} stopOpacity="1" />
                  </linearGradient>
                );
              })}
            </defs>

            {/* Leaf edges */}
            {showEdges && (compareMode ? compareLeafNodes : leafNodes).map((l) => {
              if (!isLeafVisible(l.id, l.parent)) return null;
              const p = getN(l.parent, compareMode ? "compare" : "mine");
              const hi = !selectedNode || nodeHi(l.parent);
              const isCommon = commonSet.has(l.label);
              const cc = isCommon ? getCommonColor(l.label) : null;
              return (
                <line
                  key={`le-${l.id}`}
                  x1={`${p.x}%`}
                  y1={`${p.y}%`}
                  x2={`${l.x}%`}
                  y2={`${l.y}%`}
                  stroke={`url(#cnlg-${l.id})`}
                  strokeWidth={isCommon ? 1.5 : 1}
                  opacity={isCommon ? 0.88 : hi ? 0.85 : 0.08}
                  style={{ transition: "opacity 0.25s" }}
                />
              );
            })}

            {/* Main edges – minimal */}
            {showEdges && (compareMode ? compareEdges : edges).map((e) => {
              if (!edgeVisible(e)) return null;
              const f = getN(e.from, compareMode ? "compare" : "mine");
              const t = getN(e.to, compareMode ? "compare" : "mine");
              const hi = edgeHi(e);
              const isCommon = commonSet.has(f.label) && commonSet.has(t.label);
              const cc = isCommon ? getCommonColor(f.label) : null;
              return (
                <line
                  key={`cne-base-${e.from}-${e.to}`}
                  x1={`${f.x}%`}
                  y1={`${f.y}%`}
                  x2={`${t.x}%`}
                  y2={`${t.y}%`}
                  stroke={hi ? colors.edgeNeutral : colors.edgeMuted}
                  strokeWidth={isCommon ? 1.6 : hi ? 1.25 : 1}
                  style={isCommon ? { filter: `drop-shadow(0px 0px 14px ${cc}40)`, transition: "opacity 0.25s" } : { transition: "opacity 0.25s" }}
                  opacity={1}
                />
              );
            })}
          </svg>

          {/* Leaf nodes */}
          {(compareMode ? compareLeafNodes : leafNodes).map((l) => {
            if (!isLeafVisible(l.id, l.parent)) return null;
            const hi = !selectedNode || nodeHi(l.parent);
            const isLeafSel = selectedLeaf === l.id;
            const isLeafHov = hovered === `leaf-${l.id}`;
            const isCommon = commonSet.has(l.label);
            const cc = isCommon ? getCommonColor(l.label) : l.pColor;
            const commonDeep = deepCommon(cc);
            return (
              <div
                key={l.id}
                className="absolute flex flex-col items-center cursor-pointer"
                style={{
                  left: `${l.x}%`,
                  top: `${l.y}%`,
                  transform: "translate(-50%,-50%)",
                  opacity: hi ? 1 : 0.08,
                  transition: "opacity 0.25s",
                  zIndex: isLeafSel || isLeafHov ? 30 : 5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = isLeafSel ? null : l.id;
                  setSelectedLeaf(next);
                  // also drive the page-level detail panel (treat leaf as a node)
                  onSelectNode(next);
                }}
                onMouseEnter={() => setHovered(`leaf-${l.id}`)}
                onMouseLeave={() => setHovered(null)}
                data-node="true"
              >
                <div
                  className={`rounded-full border transition-all duration-150 ${isCommon ? "cn-breathe" : ""}`}
                  style={{
                    width: isLeafSel || isLeafHov ? 14 : 11,
                    height: isLeafSel || isLeafHov ? 14 : 11,
                    background:
                      theme === "dark"
                        ? isCommon
                          ? `rgba(15,23,42,0.70)`
                          : rgbaHex(darkSolid(cc), 0.7)
                        : isCommon
                        ? commonDeep
                        : colors.leafFill,
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    borderColor: isCommon ? "transparent" : isLeafSel ? accent : colors.nodeStroke,
                    borderWidth: isCommon ? 0 : isLeafSel ? 2 : 1,
                    boxShadow:
                      theme === "dark"
                        ? isLeafSel
                          ? `0 0 18px ${cc}3A, 0 0 56px ${cc}18`
                          : isLeafHov
                          ? `0 0 12px ${cc}2A, 0 0 40px ${cc}12`
                          : "none"
                        : isCommon
                        ? `0 0 0 8px ${commonDeep}1A, 0 0 28px ${commonDeep}40`
                        : isLeafSel
                        ? "0 8px 24px rgba(17,24,39,0.10)"
                        : isLeafHov
                        ? "0 6px 18px rgba(17,24,39,0.08)"
                        : "none",
                    ["--base-scale" as any]: 1,
                  }}
                />
                <span
                  className="whitespace-nowrap mt-0.5 transition-all duration-150"
                  style={{
                    fontSize: isLeafSel || isLeafHov ? 10 : 9,
                    fontWeight: isLeafSel ? 600 : 500,
                    color: isCommon ? "rgba(255,255,255,0.92)" : isLeafSel ? accent : colors.textMuted,
                  }}
                >
                  {showLabels && l.label}
                </span>

                {/* Tooltip on select */}
                {isLeafSel && (
                  <div
                    className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white border border-[#E5E6EB] rounded-lg px-2.5 py-1.5 pointer-events-none"
                    style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", minWidth: 90, zIndex: 50 }}
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.pColor }} />
                      {showLabels && <span className="text-[11px] font-semibold text-[#1D2129]">{l.label}</span>}
                    </div>
                    <p className="text-[10px] text-[#8B8FA8] leading-relaxed">{l.parent === "relativity" ? "相对论" : l.parent === "quantum" ? "量子力学" : l.parent === "photoelectric" ? "光电效应" : l.parent === "statistical" ? "统计力学" : l.parent === "scientific" ? "科学方法" : l.parent === "cosmology" ? "宇宙论" : l.parent === "philosophy" ? "哲学" : l.parent === "math" ? "数学" : "布朗运动"} 子概念</p>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0" style={{ borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #E5E6EB" }} />
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0" style={{ borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "4px solid white", marginTop: -1 }} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Main nodes */}
          {(compareMode ? compareMainNodes : mainNodes).map((node) => {
            if (!isNodeVisible(node.id)) return null;
            const isSel = selectedMainId === node.id;
            const isHov = hovered === node.id;
            const hi = nodeHi(node.id);
            const isCommon = commonSet.has(node.label);
            const cc = isCommon ? getCommonColor(node.label) : node.color;
            const commonDeep = deepCommon(cc);
            return (
              <div
                key={node.id}
                data-node="true"
                className="absolute flex flex-col items-center cursor-pointer cn-node"
                style={{
                  left: `${node.x}%`,
                  top: `${node.y}%`,
                  transform: "translate(-50%,-50%)",
                  opacity: hi ? 1 : 0.13,
                  transition: "opacity 0.25s",
                  zIndex: isSel || isHov ? 20 : 10,
                }}
                onClick={() => {
                  setSelectedLeaf(null);
                  onSelectNode(selectedMainId === node.id ? null : node.id);
                }}
                onMouseEnter={() => setHovered(node.id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Linear-like node: white surface + subtle border + small color dot */}
                <div
                  className={`rounded-full flex items-center justify-center select-none relative ${isCommon ? "cn-breathe" : ""}`}
                  style={{
                    width: node.r * 2,
                    height: node.r * 2,
                    // bring back "colored" node background, but keep it subtle
                    background:
                      theme === "dark"
                        ? isCommon
                          ? `rgba(15,23,42,0.70)`
                          : rgbaHex(darkSolid(cc), 0.7)
                        : isCommon
                        ? commonDeep
                        : `${node.color}16`,
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    border: isCommon ? "0" : `1px solid ${colors.nodeStroke}`,
                    boxShadow: isSel
                      ? theme === "dark"
                        ? `0 22px 60px rgba(0,0,0,0.55),
                           0 0 0 1px ${cc}3A,
                           0 0 34px ${cc}44,
                           0 0 96px ${cc}22`
                        : `0 18px 40px rgba(17,24,39,0.14), 0 0 0 1px ${node.color}22, 0 10px 28px ${node.color}20`
                      : isHov
                      ? theme === "dark"
                        ? `0 18px 48px rgba(0,0,0,0.50),
                           0 0 0 1px ${cc}2A,
                           0 0 26px ${cc}38,
                           0 0 72px ${cc}18`
                        : `0 14px 30px rgba(17,24,39,0.12), 0 8px 20px ${node.color}14`
                      : isCommon
                      ? theme === "dark"
                        ? `0 18px 54px rgba(0,0,0,0.55),
                           0 0 0 1px ${cc}55,
                           0 0 34px ${cc}88,
                           0 0 120px ${cc}38`
                        : `0 0 0 10px ${commonDeep}14, 0 0 34px ${commonDeep}45, 0 14px 34px rgba(17,24,39,0.10)`
                      : theme === "dark"
                      ? `0 14px 40px rgba(0,0,0,0.55),
                         0 0 0 1px ${node.color}18,
                         0 0 18px ${node.color}22`
                      : "0 8px 20px rgba(17,24,39,0.06)",
                    ["--base-scale" as any]: isSel ? 1.08 : isHov ? 1.04 : 1,
                    transition: "transform 0.18s ease, background 0.18s ease, border-color 0.18s ease",
                  }}
                >
                  <span
                    className="font-semibold text-center leading-tight relative"
                    style={{
                      fontSize: node.r > 35 ? 13 : 11,
                      maxWidth: node.r * 1.7,
                      textAlign: "center",
                      color: isCommon ? "rgba(255,255,255,0.92)" : colors.text,
                    }}
                  >
                    {showLabels && node.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Zoom indicator */}
        <div className="absolute bottom-3 right-3 bg-white border border-[#E5E6EB] rounded-lg px-2.5 py-1 pointer-events-none">
          <span className="text-[11px] text-[#9699A6] font-mono">{Math.round(zoom * 100)}%</span>
        </div>

        {/* Bottom hint */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white border border-[#E5E6EB] rounded-full px-3 py-1.5 pointer-events-none">
          <i className="ri-drag-move-line text-[11px] text-[#9699A6]"></i>
          <span className="text-[11px] text-[#9699A6]">拖拽平移 · 滚轮缩放 · 点击节点查看详情</span>
        </div>
      </div>
    );
  }
);

CogNetGraphCanvas.displayName = "CogNetGraphCanvas";
export default CogNetGraphCanvas;
